import { useEffect, useCallback, useRef } from 'react';
import { useLazyGetCustomerOrdersQuery } from '@api/shipping-data/detailApi';
import ErrorElement from 'components/base-elements/error/ErrorElement';
import LoadingElement from 'components/base-elements/loading/LoadingElement';
import CollapsedBlock from 'components/collapsed-block/CollapsedBlock';
import Orders from '@pages/orders/detail/tabs-pages/basic-overview/orders/Orders';
import MapComponent from '@pages/orders/detail/tabs-pages/basic-overview/map/Map';
import MaterialGrids from '@pages/orders/detail/tabs-pages/basic-overview/material-grids/MaterialGrids';



interface BasicOverviewProps {
	destinationUnvId: string,
	selectedOrderUnvId: string | null,
	selectedOrderDateWithoutTime: string,
	isSmallScreen: boolean,
}


export const BasicOverview = (props: BasicOverviewProps) => {
	const { destinationUnvId, selectedOrderUnvId, selectedOrderDateWithoutTime, isSmallScreen } = props;

	const ordersRef = useRef<Map<string, HTMLDivElement>>(null);
	const [ getCustomerOrders, { data, isLoading, isFetching, isError }] = useLazyGetCustomerOrdersQuery();


	useEffect(() => {
		if (destinationUnvId && selectedOrderDateWithoutTime) {
			getCustomerOrders({ date: selectedOrderDateWithoutTime, destinationUnvSysId: destinationUnvId }, true);
		}
	}, [ destinationUnvId, selectedOrderDateWithoutTime ]);


	const saveNodeRefToOrdersMap = useCallback((node: HTMLDivElement | null, orderUnvId: string) => {
		if (!ordersRef.current) {
			ordersRef.current = new Map();
		}
		
		if (node) {
			ordersRef.current.set(orderUnvId, node);
		} else {
			ordersRef.current.delete(orderUnvId);
		}
	},[ ordersRef ]);


	const scrollToOrderGrid = useCallback((orderUnvId: string) => {
		if (!data || data.length === 0) return;

		const nodesMap = ordersRef.current;
		if (!nodesMap) return;

		const node = nodesMap.get(orderUnvId);
		if (!node) return;
		
		node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
	},[ ordersRef, data ]);


	if (isError) return (
		<div className='detail-area-empty'>
			<ErrorElement />
		</div>
	);

	
	if (isLoading || isFetching) return (
		<div className='detail-area-empty'>
			<LoadingElement />
		</div>
	);

	
	if (!data || data.length === 0) return (
		<p className='detail-area-empty'>Žádná data k dispozici</p>
	);


	return (
		<>
			<CollapsedBlock
				label={'Základní přehled'}
				element={
					<Orders
						orders={data}
						selectedOrderUnvId={selectedOrderUnvId}
						scrollToOrderGrid={scrollToOrderGrid} />
				} />
			
			<CollapsedBlock
				label={'Mapa'}
				openOnInit={!isSmallScreen}
				isDisabled={true}
				element={<MapComponent />} />
			
			<CollapsedBlock
				label={'Položky'}
				openOnInit={!isSmallScreen}
				element={
					<MaterialGrids
						orders={data}
						selectedOrderUnvId={selectedOrderUnvId}
						saveNodeRefToOrdersMap={saveNodeRefToOrdersMap} />
				} />
		</>
	)
}